// Phase 17 — typed asset manifest.
// Every Phase 17 sprite key used by the game appears here.
// BootScene.preload() iterates this manifest; missing PNGs fall back to shape rendering.

export interface AssetEntry {
  /** Phaser texture key */
  key: string;
  /** Path relative to public/ */
  path: string;
  /** Fallback strategy when load fails */
  fallback: 'shape';
}

/**
 * ASSET_MANIFEST — complete list of Phase 17 sprite assets.
 * Keys must match the pattern used by runtime consumers (FicheTray, Wheel, scenes).
 * Legacy keys (fiche-*, ball-*, relic-*, croupier, shopkeeper) are loaded directly
 * in BootScene and are NOT listed here — they predate the manifest system.
 */
export const ASSET_MANIFEST: readonly AssetEntry[] = [
  // ── Portraits ────────────────────────────────────────────
  { key: 'portrait-dealer', path: 'assets/sprites/portrait-dealer.png', fallback: 'shape' },
  { key: 'portrait-shopkeeper', path: 'assets/sprites/portrait-shopkeeper.png', fallback: 'shape' },
  // Boss portraits (one per modifier type)
  {
    key: 'portrait-boss-no-even-money',
    path: 'assets/sprites/portrait-boss-no-even-money.png',
    fallback: 'shape',
  },
  {
    key: 'portrait-boss-no-straights',
    path: 'assets/sprites/portrait-boss-no-straights.png',
    fallback: 'shape',
  },
  {
    key: 'portrait-boss-half-payouts',
    path: 'assets/sprites/portrait-boss-half-payouts.png',
    fallback: 'shape',
  },

  // ── Fiche inner motifs (4 rarity tiers — fallback when no per-fiche sprite) ──
  { key: 'chip-common', path: 'assets/sprites/chip-common.png', fallback: 'shape' },
  { key: 'chip-uncommon', path: 'assets/sprites/chip-uncommon.png', fallback: 'shape' },
  { key: 'chip-rare', path: 'assets/sprites/chip-rare.png', fallback: 'shape' },
  { key: 'chip-legendary', path: 'assets/sprites/chip-legendary.png', fallback: 'shape' },

  // ── Per-fiche sprites (128×128, key = fiche-{defId}) ─────
  { key: 'fiche-standard', path: 'assets/sprites/fiche-standard.png', fallback: 'shape' },
  { key: 'fiche-lucky7', path: 'assets/sprites/fiche-lucky7.png', fallback: 'shape' },
  { key: 'fiche-red_hot', path: 'assets/sprites/fiche-red_hot.png', fallback: 'shape' },
  { key: 'fiche-cold_steel', path: 'assets/sprites/fiche-cold_steel.png', fallback: 'shape' },
  { key: 'fiche-even_steven', path: 'assets/sprites/fiche-even_steven.png', fallback: 'shape' },
  { key: 'fiche-gambler', path: 'assets/sprites/fiche-gambler.png', fallback: 'shape' },
  { key: 'fiche-pair_o_dice', path: 'assets/sprites/fiche-pair_o_dice.png', fallback: 'shape' },
  { key: 'fiche-house_special', path: 'assets/sprites/fiche-house_special.png', fallback: 'shape' },
  { key: 'fiche-chess_pawn', path: 'assets/sprites/fiche-chess_pawn.png', fallback: 'shape' },
  { key: 'fiche-chess_knight', path: 'assets/sprites/fiche-chess_knight.png', fallback: 'shape' },
  { key: 'fiche-chess_bishop', path: 'assets/sprites/fiche-chess_bishop.png', fallback: 'shape' },
  { key: 'fiche-chess_rook', path: 'assets/sprites/fiche-chess_rook.png', fallback: 'shape' },
  { key: 'fiche-chess_king', path: 'assets/sprites/fiche-chess_king.png', fallback: 'shape' },
  { key: 'fiche-chess_queen', path: 'assets/sprites/fiche-chess_queen.png', fallback: 'shape' },
  {
    key: 'fiche-hot_streak_rider',
    path: 'assets/sprites/fiche-hot_streak_rider.png',
    fallback: 'shape',
  },
  { key: 'fiche-chain_gambler', path: 'assets/sprites/fiche-chain_gambler.png', fallback: 'shape' },
  { key: 'fiche-zero_chaser', path: 'assets/sprites/fiche-zero_chaser.png', fallback: 'shape' },
  { key: 'fiche-fiche_hoarder', path: 'assets/sprites/fiche-fiche_hoarder.png', fallback: 'shape' },
  { key: 'fiche-cold_front', path: 'assets/sprites/fiche-cold_front.png', fallback: 'shape' },
  { key: 'fiche-column_shark', path: 'assets/sprites/fiche-column_shark.png', fallback: 'shape' },
  { key: 'fiche-corner_king', path: 'assets/sprites/fiche-corner_king.png', fallback: 'shape' },
  { key: 'fiche-dozen_devotee', path: 'assets/sprites/fiche-dozen_devotee.png', fallback: 'shape' },
  { key: 'fiche-split_shot', path: 'assets/sprites/fiche-split_shot.png', fallback: 'shape' },
  { key: 'fiche-cell_swapper', path: 'assets/sprites/fiche-cell_swapper.png', fallback: 'shape' },
  { key: 'fiche-tetris_i', path: 'assets/sprites/fiche-tetris_i.png', fallback: 'shape' },
  { key: 'fiche-tetris_o', path: 'assets/sprites/fiche-tetris_o.png', fallback: 'shape' },
  { key: 'fiche-tetris_t', path: 'assets/sprites/fiche-tetris_t.png', fallback: 'shape' },
  { key: 'fiche-tetris_s', path: 'assets/sprites/fiche-tetris_s.png', fallback: 'shape' },
  { key: 'fiche-tetris_z', path: 'assets/sprites/fiche-tetris_z.png', fallback: 'shape' },
  { key: 'fiche-tetris_j', path: 'assets/sprites/fiche-tetris_j.png', fallback: 'shape' },
  { key: 'fiche-tetris_l', path: 'assets/sprites/fiche-tetris_l.png', fallback: 'shape' },

  // ── Ball sprites (128×128, key = ball-{defId}) ───────────
  { key: 'ball-standard_ball', path: 'assets/sprites/ball-standard_ball.png', fallback: 'shape' },
  { key: 'ball-red_ball', path: 'assets/sprites/ball-red_ball.png', fallback: 'shape' },
  { key: 'ball-magnet_ball', path: 'assets/sprites/ball-magnet_ball.png', fallback: 'shape' },
  { key: 'ball-lucky_ball', path: 'assets/sprites/ball-lucky_ball.png', fallback: 'shape' },
  { key: 'ball-sprite', path: 'assets/sprites/ball-sprite.png', fallback: 'shape' },
  { key: 'ball-splash_ball', path: 'assets/sprites/ball-splash_ball.png', fallback: 'shape' },

  // ── Relic sprites (128×128, key = relic-{defId}) ─────────
  {
    key: 'relic-croupiers_smile',
    path: 'assets/sprites/relic-croupiers_smile.png',
    fallback: 'shape',
  },
  { key: 'relic-lucky_charm', path: 'assets/sprites/relic-lucky_charm.png', fallback: 'shape' },
  { key: 'relic-house_edge', path: 'assets/sprites/relic-house_edge.png', fallback: 'shape' },
  { key: 'relic-cold_streak', path: 'assets/sprites/relic-cold_streak.png', fallback: 'shape' },
  { key: 'relic-hot_streak', path: 'assets/sprites/relic-hot_streak.png', fallback: 'shape' },
  { key: 'relic-safety_net', path: 'assets/sprites/relic-safety_net.png', fallback: 'shape' },
  { key: 'relic-second_ball', path: 'assets/sprites/relic-second_ball.png', fallback: 'shape' },
  {
    key: 'relic-compound_interest',
    path: 'assets/sprites/relic-compound_interest.png',
    fallback: 'shape',
  },
  { key: 'relic-peek', path: 'assets/sprites/relic-peek.png', fallback: 'shape' },
  { key: 'relic-void_walker', path: 'assets/sprites/relic-void_walker.png', fallback: 'shape' },
  { key: 'relic-reliable', path: 'assets/sprites/relic-reliable.png', fallback: 'shape' },
  { key: 'relic-golden_touch', path: 'assets/sprites/relic-golden_touch.png', fallback: 'shape' },
  { key: 'relic-one_for_all', path: 'assets/sprites/relic-one_for_all.png', fallback: 'shape' },

  // ── Background tile ───────────────────────────────────────
  // ── Consumable sprites (128×128, key = consumable-{defId}) ──
  { key: 'consumable-bleach', path: 'assets/sprites/consumable-bleach.png', fallback: 'shape' },
  { key: 'consumable-compound', path: 'assets/sprites/consumable-compound.png', fallback: 'shape' },
  {
    key: 'consumable-double_or_nothing',
    path: 'assets/sprites/consumable-double_or_nothing.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-flip_color',
    path: 'assets/sprites/consumable-flip_color.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-loaded_ball',
    path: 'assets/sprites/consumable-loaded_ball.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-loose_change',
    path: 'assets/sprites/consumable-loose_change.png',
    fallback: 'shape',
  },
  { key: 'consumable-merge', path: 'assets/sprites/consumable-merge.png', fallback: 'shape' },
  { key: 'consumable-mint', path: 'assets/sprites/consumable-mint.png', fallback: 'shape' },
  {
    key: 'consumable-shuffle_columns',
    path: 'assets/sprites/consumable-shuffle_columns.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-shuffle_rows',
    path: 'assets/sprites/consumable-shuffle_rows.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-skip_slot',
    path: 'assets/sprites/consumable-skip_slot.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-swap_cells',
    path: 'assets/sprites/consumable-swap_cells.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-gold_dust',
    path: 'assets/sprites/consumable-gold_dust.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-gilded_rim',
    path: 'assets/sprites/consumable-gilded_rim.png',
    fallback: 'shape',
  },
  {
    key: 'consumable-amplifier',
    path: 'assets/sprites/consumable-amplifier.png',
    fallback: 'shape',
  },

  { key: 'bg-tile', path: 'assets/sprites/bg-tile.png', fallback: 'shape' },
  { key: 'bg-homescreen', path: 'assets/sprites/bg-homescreen.png', fallback: 'shape' },

  // ── Wheel ─────────────────────────────────────────────────
  { key: 'wheel-sprite', path: 'assets/sprites/wheel-sprite.png', fallback: 'shape' },
  { key: 'wheel-ring', path: 'assets/sprites/wheel-ring.png', fallback: 'shape' },
  { key: 'wheel-hub', path: 'assets/sprites/wheel-hub.png', fallback: 'shape' },
];
